import {Button, Fab, MenuItem, useMediaQuery, Zoom} from '@material-ui/core';
import {AgentInformationRouteConstants} from 'Agent/AgentInformation/AgentInformationRouteConstants';
import 'Agent/AgentInformation/components/Hierarchy.sass';
import SelectAgentNumberModal from 'Agent/AgentInformation/components/SelectAgentNumberModal';
import {getHierarchyByAgentNumber} from 'Agent/AgentInformation/ducks/hierarchy';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import {toAsync, toEither, toPage} from 'Core/models/Async';
import * as A from 'fp-ts/lib/Array';
import * as E from 'fp-ts/lib/Either';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import qs from 'query-string';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps, useHistory, useLocation} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import OutlinedSelect from 'Shared/components/OutlinedSelect';
import Column from 'Shared/components/PagedView/Column';
import PagedView from 'Shared/components/PagedView/PagedView';
import {AgencyFilters} from 'Shared/models/AgencyFilters';
import {curry} from 'Shared/utilities/curry';
import {NoOp} from 'Shared/utilities/IOUtilities';
import {prop} from 'Shared/utilities/prop';

type HierarchyProps = {
  selectedAgency: AgencyFilters;
} & RouteComponentProps;

const Hierarchy = ({match, selectedAgency}: HierarchyProps) => {
  const dispatch = useDispatch();
  useBreadcrumbs('Agent Hierarchy', match.url);
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('lg'));
  const history = useHistory();
  const {search} = useLocation();
  const agentNumber = qs.parse(search).agentNumber as string | undefined;
  const asyncHierarchy = useSelector((state: ReducerState) => state.hierarchy);
  const getDefaultAgentNumber = () => pipe(selectedAgency, prop('agentNumbers'), A.head);

  const [selectedAgentNumberOption, setSelectedAgentNumber] = useState(getDefaultAgentNumber);
  const [isAgencyDialogVisible, setAgencyDialogVisible] = useState(false);
  const currentAgentNumbers = selectedAgency.agentNumbers;

  useEffect(() => {
    const getHierarchyWithCurrentAgency = curry(getHierarchyByAgentNumber)(selectedAgency);
    const currentAgentNumber = O.getFirstMonoid<string>().concat(
      O.fromNullable(agentNumber),
      selectedAgentNumberOption
    );

    pipe(O.some(getHierarchyWithCurrentAgency), O.ap(currentAgentNumber), O.fold(NoOp, dispatch));
  }, [selectedAgentNumberOption, agentNumber]);

  useEffect(() => {
    setSelectedAgentNumber(getDefaultAgentNumber);
  }, [selectedAgency]);

  if (O.isNone(selectedAgentNumberOption)) {
    return <div>No Agent Numbers Available</div>;
  }

  const selectedAgentNumber = selectedAgentNumberOption.value;

  const renderDesktopActions = () => (
    <div className="hierarchy__actions">
      {agentNumber && (
        <>
          <span className="hierarchy__results-message">
            <strong>Showing Results for Agent Number:</strong>
            &nbsp;{agentNumber}
          </span>
          <Button variant="text" color="primary" className="hierarchy__return-button" onClick={history.goBack}>
            Return
          </Button>
        </>
      )}
      <OutlinedSelect
        label="Agent Number"
        className="hierarchy__agent-number-select"
        value={selectedAgentNumber}
        onChange={e => {
          history.push(`${AgentInformationRouteConstants.HIERARCHY}`);
          setSelectedAgentNumber(O.some(e.target.value));
        }}
      >
        {currentAgentNumbers.map(agentNumber => (
          <MenuItem value={agentNumber}>{agentNumber}</MenuItem>
        ))}
      </OutlinedSelect>
    </div>
  );

  const renderMobileActions = () => (
    <Zoom in>
      <Fab
        className="hierarchy__select-agent-number-button"
        onClick={() => setAgencyDialogVisible(true)}
        variant="extended"
      >
        Select Agent Number
      </Fab>
    </Zoom>
  );

  const renderActions = isMobile ? renderMobileActions : renderDesktopActions;
  const mobileResultsMessage = isMobile && agentNumber && (
    <span className="hierarchy__mobile-results-message">
      <strong>Showing Results for Agent Number:</strong>
      &nbsp;{agentNumber}
    </span>
  );

  const asyncPageOfEmployees = pipe(
    toEither(asyncHierarchy),
    E.map(prop('levelOneEmployees')),
    E.map(value => value || []),
    E.map(toPage),
    toAsync
  );

  return (
    <>
      {mobileResultsMessage}
      <PagedView pagedViewKey="hierarchy" loadMore={() => {}} data={asyncPageOfEmployees} actions={renderActions()}>
        {columnProps => (
          <>
            <Column {...columnProps} title="Agent Name" default>
              {employee => employee.agentName}
            </Column>
            <Column {...columnProps} title="Agent Number" default>
              {employee => employee.agentNumber}
            </Column>
            <Column {...columnProps} isActions title="" default>
              {employee =>
                employee.link ? (
                  <Button
                    color="primary"
                    fullWidth={isMobile}
                    onClick={() => {
                      history.push(`${AgentInformationRouteConstants.HIERARCHY}?agentNumber=${employee.agentNumber}`);
                    }}
                  >
                    View Employees
                  </Button>
                ) : (
                  ' '
                )
              }
            </Column>
          </>
        )}
      </PagedView>
      {isMobile && (
        <SelectAgentNumberModal
          onChange={num => {
            history.push(AgentInformationRouteConstants.HIERARCHY);
            setSelectedAgentNumber(O.some(num));
          }}
          onClose={() => setAgencyDialogVisible(false)}
          open={isAgencyDialogVisible}
          value={selectedAgentNumber}
        />
      )}
    </>
  );
};

export default Hierarchy;
